import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzySearchList } from '../lib/fuzzySearch';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Cell,
} from 'recharts';
import {
  getReport,
  generateReport,
  backfillReportLineItems,
} from '../api/client';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import DailySummarySettings from '../components/DailySummarySettings';
import ReportCustomerPresetControls from '../components/ReportCustomerPresetControls';
import { aggregateSelectedItemSales } from '../lib/itemSalesAnalytics';
import type { ItemGroupSale } from '../lib/itemSalesAnalytics';
import { normalizeItemOrderForecastSummaries } from '../lib/reportNormalization';
import {
  matchesNumericRange,
  sortCustomerAnalysisRows,
} from '../lib/reportCustomerAnalysis';
import type { CustomerAnalysisSortKey } from '../lib/reportCustomerAnalysis';
import {
  defaultTrackedItemFilters,
  matchesSharpDeclinePreset,
} from '../lib/reportCustomerPresets';
import type {
  TrackedItemColumnFilters,
  TrackedItemGrowthFilter,
} from '../lib/reportCustomerPresets';

// ── Types ────────────────────────────────────────────────────────────

interface DebtKpiPoint {
  month: string;
  old_debt_amount: number;
  total_debt_amount: number;
  old_debt_ratio: number;
}

interface RevenueMonthPoint {
  month: string;
  revenue: number;
  projected_revenue: number | null;
  is_projection: boolean;
}

interface CustomerTrackedItemMonth {
  month: string;
  quantity: number;
  is_current?: boolean;
}

interface CustomerBreakdown {
  customer_id: number;
  customer_name: string;
  tracked_item_quantity: number;
  tracked_item_groups: string[];
  tracked_item_avg_monthly_3m: number;
  tracked_item_avg_monthly_6m: number;
  tracked_item_growth_pct: number | null;
  tracked_item_growth_status: 'growing' | 'struggling' | 'stable' | string;
  tracked_item_monthly: CustomerTrackedItemMonth[];
  last_tracked_order_date?: string | null;
  last_tracked_order_quantity?: number | null;
  predicted_next_order_date?: string | null;
  days_until_next_order?: number | null;
  aged_debt_amount: number;
  unpaid_amount: number;
}

interface CustomerItemOrderForecast {
  customer_id: number;
  customer_name: string;
  item_id: number;
  item_name: string;
  predicted_order_date: string;
  days_until_order: number;
  predicted_quantity: number;
  last_order_date: string;
  last_order_quantity: number;
  days_since_last_order: number;
  median_cadence_days: number;
  history_order_count: number;
  quality: 'high' | 'medium' | 'low' | string;
  churned: boolean;
}

interface ItemOrderForecastSummary {
  item_id: number;
  item_name: string;
  catalog_number?: string | null;
  item_group: string;
  horizon_days: number;
  next_7_days_quantity: number;
  total_predicted_quantity: number;
  overdue_quantity: number;
  earliest_predicted_date?: string | null;
  customers_due_7d_count: number;
  customers_due_count: number;
  insufficient_history_customers: number;
  churned_customers_count: number;
  customer_forecasts: CustomerItemOrderForecast[];
  churned_customers: CustomerItemOrderForecast[];
}

interface ReportData {
  status?: string;
  message?: string;
  generated_at?: string;
  period: string;
  from_date: string;
  to_date: string;
  total_old_debt: number | null;
  total_all_debt: number | null;
  old_debt_ratio: number | null;
  prev_old_debt_ratio: number | null;
  revenue_to_date: number | null;
  projected_revenue_year_end: number | null;
  debt_kpi_history: DebtKpiPoint[];
  monthly_revenue: RevenueMonthPoint[];
  customer_breakdown: CustomerBreakdown[];
  item_group_sales: ItemGroupSale[];
  item_order_forecasts: ItemOrderForecastSummary[];
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatNIS(amount: number | null | undefined): string {
  if (amount == null) return '₪—';
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatFilterDate(iso: string, locale?: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ── Sub-components ───────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  prevValue,
  format = 'number',
  loading = false,
  invertChange = false,
  t,
}: {
  label: string;
  value: number;
  prevValue?: number | null;
  format?: 'number' | 'currency' | 'percent';
  loading?: boolean;
  invertChange?: boolean;
  t: (key: string) => string;
}) {
  const display = format === 'currency'
    ? formatNIS(value)
    : format === 'percent'
      ? `${value.toFixed(1)}%`
      : value.toLocaleString();
  const change = prevValue != null && prevValue !== 0
    ? ((value - prevValue) / prevValue) * 100
    : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-1">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      {loading ? (
        <>
          <span className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
          <span className="mt-1 h-4 w-24 animate-pulse rounded-md bg-gray-100" />
        </>
      ) : (
        <span className="text-2xl font-bold text-gray-900">{display}</span>
      )}
      {!loading && change !== null && (
        <span className={`text-xs font-medium flex items-center gap-1 ${(invertChange ? change <= 0 : change >= 0) ? 'text-green-600' : 'text-red-500'}`}>
          {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
          <span className="text-gray-400 font-normal ltr:ml-1 rtl:mr-1">{t('reports.vsLastPeriod')}</span>
        </span>
      )}
    </div>
  );
}

function Sparkline({ points }: { points: CustomerTrackedItemMonth[] }) {
  const width = 112;
  const height = 34;
  const values = points.map((point) => point.quantity);
  const max = Math.max(...values, 1);
  const step = points.length > 1 ? width / (points.length - 1) : width;

  const coords = values.map((value, index) => ({
    x: index * step,
    y: height - (value / max) * (height - 4) - 2,
  }));

  const lastIsCurrent = points.length > 0 && points[points.length - 1].is_current;
  const solidEnd = lastIsCurrent ? coords.length - 1 : coords.length;
  const solidPath = coords
    .slice(0, solidEnd)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const dottedPath = lastIsCurrent && coords.length >= 2
    ? `M ${coords[coords.length - 2].x.toFixed(1)} ${coords[coords.length - 2].y.toFixed(1)} L ${coords[coords.length - 1].x.toFixed(1)} ${coords[coords.length - 1].y.toFixed(1)}`
    : '';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden="true">
      <path d={solidPath || `M 0 ${height - 2}`} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {dottedPath && (
        <path d={dottedPath} fill="none" stroke="#2563eb" strokeWidth="2" strokeDasharray="3 3" strokeLinecap="round" />
      )}
      {coords.map((p, index) => {
        const isCurrent = points[index]?.is_current;
        return isCurrent ? (
          <circle key={`${index}-${values[index]}`} cx={p.x} cy={p.y} r="2.5" fill="white" stroke="#2563eb" strokeWidth="1.5" />
        ) : (
          <circle key={`${index}-${values[index]}`} cx={p.x} cy={p.y} r="2" fill="#2563eb" />
        );
      })}
    </svg>
  );
}

const TRACKED_ITEM_PAGE_SIZE = 20;

type TrackedItemSortKey = CustomerAnalysisSortKey;

function TrackedItemConsumptionTable({
  data,
  onOpenCustomer,
  t,
}: {
  data: CustomerBreakdown[];
  onOpenCustomer: (customer: CustomerBreakdown) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [sortKey, setSortKey] = useState<TrackedItemSortKey>('tracked_item_avg_monthly_3m');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filters, setFilters] = useState<TrackedItemColumnFilters>(defaultTrackedItemFilters);
  const [activePresetId, setActivePresetId] = useState('all');
  const [strictBuiltInPreset, setStrictBuiltInPreset] = useState(false);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setPage(0);
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  const rows = useMemo(
    () => data.filter((customer) => (customer.tracked_item_quantity ?? 0) > 0),
    [data],
  );

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const searched = useMemo(() => {
    if (!debouncedQuery.trim()) return rows;
    return fuzzySearchList(rows, debouncedQuery, (c) => [
      c.customer_name,
      ...(c.tracked_item_groups ?? []),
    ]);
  }, [rows, debouncedQuery]);

  const filtered = useMemo(() => {
    const customerFilter = filters.customer.trim().toLocaleLowerCase();
    const itemGroupFilter = filters.itemGroup.trim().toLocaleLowerCase();

    return searched.filter((customer) => {
      const name = (customer.customer_name || `#${customer.customer_id}`).toLocaleLowerCase();
      const itemGroups = (customer.tracked_item_groups ?? []).join(' ').toLocaleLowerCase();
      const growthStatus = (customer.tracked_item_growth_status || 'stable') as TrackedItemGrowthFilter;

      if (strictBuiltInPreset && !matchesSharpDeclinePreset(customer)) return false;
      if (customerFilter && !name.includes(customerFilter)) return false;
      if (itemGroupFilter && !itemGroups.includes(itemGroupFilter)) return false;
      if (!matchesNumericRange(customer.tracked_item_quantity, filters.quantityMin, filters.quantityMax)) return false;
      if (!matchesNumericRange(customer.aged_debt_amount, filters.oldDebtMin, filters.oldDebtMax)) return false;
      if (!matchesNumericRange(customer.unpaid_amount, filters.totalDebtMin, filters.totalDebtMax)) return false;
      if (!matchesNumericRange(customer.tracked_item_avg_monthly_3m, filters.avg3mMin, filters.avg3mMax)) return false;
      if (!matchesNumericRange(customer.tracked_item_avg_monthly_6m, filters.avg6mMin, filters.avg6mMax)) return false;
      if (filters.growthStatus !== 'all' && growthStatus !== filters.growthStatus) return false;
      if (!matchesNumericRange(customer.tracked_item_growth_pct ?? 0, filters.growthMin, filters.growthMax)) return false;
      if (filters.nextOrderMin.trim() || filters.nextOrderMax.trim()) {
        if (customer.days_until_next_order == null) return false;
        if (!matchesNumericRange(customer.days_until_next_order, filters.nextOrderMin, filters.nextOrderMax)) return false;
      }
      return true;
    });
  }, [searched, filters, strictBuiltInPreset]);

  const sorted = useMemo(() => {
    // When a search query is active preserve fuzzy-match score order for the name column.
    if (debouncedQuery.trim() && sortKey === 'customer_name') return filtered;
    return sortCustomerAnalysisRows(filtered, sortKey, sortDir);
  }, [filtered, sortKey, sortDir, debouncedQuery]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / TRACKED_ITEM_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(safePage * TRACKED_ITEM_PAGE_SIZE, (safePage + 1) * TRACKED_ITEM_PAGE_SIZE);
  const fromRow = sorted.length === 0 ? 0 : safePage * TRACKED_ITEM_PAGE_SIZE + 1;
  const toRow = Math.min((safePage + 1) * TRACKED_ITEM_PAGE_SIZE, sorted.length);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(0);
  };

  const updateFilter = (key: keyof TrackedItemColumnFilters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setActivePresetId('all');
    setStrictBuiltInPreset(false);
  };

  const resetPresetCriteria = () => {
    setFilters(defaultTrackedItemFilters);
    setActivePresetId('all');
    setStrictBuiltInPreset(false);
    setPage(0);
  };

  const applyPreset = (
    presetId: string,
    nextFilters: TrackedItemColumnFilters,
    strictBuiltIn: boolean,
  ) => {
    if (activePresetId === presetId) {
      resetPresetCriteria();
      return;
    }
    setFilters(nextFilters);
    setActivePresetId(presetId);
    setStrictBuiltInPreset(strictBuiltIn);
    setPage(0);
  };

  const clearFilters = () => {
    resetPresetCriteria();
    setSearchQuery('');
    setDebouncedQuery('');
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => (
    key === 'growthStatus' ? value !== 'all' : Boolean(String(value).trim())
  )).length + (debouncedQuery.trim() ? 1 : 0);

  const SortArrow = ({ col }: { col: typeof sortKey }) =>
    sortKey === col ? <span className="ltr:ml-1 rtl:mr-1">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  const growthClasses: Record<string, string> = {
    growing: 'bg-green-50 text-green-700 border-green-200',
    struggling: 'bg-red-50 text-red-700 border-red-200',
    stable: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={t(expanded ? 'reports.collapseSection' : 'reports.expandSection', { section: t('reports.trackedItemConsumption') })}
          className="flex items-center gap-2 text-start"
        >
          <span className="text-gray-400">{expanded ? '▾' : '▸'}</span>
          <h3 className="text-base font-semibold text-gray-800">{t('reports.trackedItemConsumption')}</h3>
        </button>
        {expanded && (
          <div className="relative w-full sm:w-72">
            <svg className="pointer-events-none absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('reports.trackedItemSearchPlaceholder')}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
        )}
      </div>
      {expanded && (
      <>
      <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4">
        <ReportCustomerPresetControls
          filters={filters}
          activePresetId={activePresetId}
          onApply={applyPreset}
          onReset={resetPresetCriteria}
        />
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {/* Col 1 — Customer + Item Group (item groups appear under customer name in the table cell) */}
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              {t('reports.customerName')}
              <input
                type="text"
                value={filters.customer}
                onChange={(e) => updateFilter('customer', e.target.value)}
                placeholder={t('reports.filterCustomer')}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              {t('reports.filterItemGroup')}
              <input
                type="text"
                value={filters.itemGroup}
                onChange={(e) => updateFilter('itemGroup', e.target.value)}
                placeholder={t('reports.filterItemGroupPlaceholder')}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </label>
          </div>
          {/* Col 2 — Tracked quantity */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.trackedItemQuantity')}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={filters.quantityMin}
                onChange={(e) => updateFilter('quantityMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="decimal"
                value={filters.quantityMax}
                onChange={(e) => updateFilter('quantityMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          {/* Col 3 — Old debt */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.oldDebt')}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                inputMode="decimal"
                data-testid="old-debt-min-filter"
                aria-label={`${t('reports.oldDebt')} ${t('reports.filterMin')}`}
                value={filters.oldDebtMin}
                onChange={(e) => updateFilter('oldDebtMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="decimal"
                data-testid="old-debt-max-filter"
                aria-label={`${t('reports.oldDebt')} ${t('reports.filterMax')}`}
                value={filters.oldDebtMax}
                onChange={(e) => updateFilter('oldDebtMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          {/* Col 4 — Total debt */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.totalDebt')}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                inputMode="decimal"
                data-testid="total-debt-min-filter"
                aria-label={`${t('reports.totalDebt')} ${t('reports.filterMin')}`}
                value={filters.totalDebtMin}
                onChange={(e) => updateFilter('totalDebtMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="decimal"
                data-testid="total-debt-max-filter"
                aria-label={`${t('reports.totalDebt')} ${t('reports.filterMax')}`}
                value={filters.totalDebtMax}
                onChange={(e) => updateFilter('totalDebtMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          {/* Col 5 — Avg monthly 3m */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.avgMonthly3m')}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={filters.avg3mMin}
                onChange={(e) => updateFilter('avg3mMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="decimal"
                value={filters.avg3mMax}
                onChange={(e) => updateFilter('avg3mMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          {/* Col 6 — Avg monthly 6m */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.avgMonthly6m')}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={filters.avg6mMin}
                onChange={(e) => updateFilter('avg6mMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="decimal"
                value={filters.avg6mMax}
                onChange={(e) => updateFilter('avg6mMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          {/* Col 7 — Next order (days) */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.filterNextOrder')}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                inputMode="numeric"
                value={filters.nextOrderMin}
                onChange={(e) => updateFilter('nextOrderMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="numeric"
                value={filters.nextOrderMax}
                onChange={(e) => updateFilter('nextOrderMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          {/* Col 8 — Growth indicator */}
          <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('reports.growthIndicator')}
            <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2">
              <select
                value={filters.growthStatus}
                onChange={(e) => updateFilter('growthStatus', e.target.value)}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm font-normal text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                <option value="all">{t('reports.filterAllGrowth')}</option>
                <option value="growing">{t('reports.growth.growing')}</option>
                <option value="stable">{t('reports.growth.stable')}</option>
                <option value="struggling">{t('reports.growth.struggling')}</option>
              </select>
              <input
                type="number"
                inputMode="decimal"
                value={filters.growthMin}
                onChange={(e) => updateFilter('growthMin', e.target.value)}
                placeholder={t('reports.filterMin')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number"
                inputMode="decimal"
                data-testid="growth-max-filter"
                value={filters.growthMax}
                onChange={(e) => updateFilter('growthMax', e.target.value)}
                placeholder={t('reports.filterMax')}
                className="min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm font-normal text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-500">
            <span>
              {t(activeFilterCount === 1 ? 'reports.activeFilterSingular' : 'reports.activeFilterPlural', { count: activeFilterCount })}
            </span>
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-50"
            >
              {t('reports.clearFilters')}
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500">
              <th className="px-5 py-3 text-start font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('customer_name')}>
                {t('reports.customerName')}
                <SortArrow col="customer_name" />
              </th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('aged_debt_amount')}>
                {t('reports.oldDebt')}
                <SortArrow col="aged_debt_amount" />
              </th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('unpaid_amount')}>
                {t('reports.totalDebt')}
                <SortArrow col="unpaid_amount" />
              </th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('tracked_item_quantity')}>
                {t('reports.trackedItemQuantity')}
                <SortArrow col="tracked_item_quantity" />
              </th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('tracked_item_avg_monthly_3m')}>
                {t('reports.avgMonthly3m')}
                <SortArrow col="tracked_item_avg_monthly_3m" />
              </th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('tracked_item_avg_monthly_6m')}>
                {t('reports.avgMonthly6m')}
                <SortArrow col="tracked_item_avg_monthly_6m" />
              </th>
              <th className="px-5 py-3 text-center font-medium">{t('reports.averageTrend')}</th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('tracked_item_growth_pct')}>
                {t('reports.growthIndicator')}
                <SortArrow col="tracked_item_growth_pct" />
              </th>
              <th className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700" onClick={() => toggleSort('days_until_next_order')}>
                {t('reports.nextOrderDays')}
                <SortArrow col="days_until_next_order" />
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((customer) => {
              const growthStatus = customer.tracked_item_growth_status || 'stable';
              const growth = customer.tracked_item_growth_pct ?? 0;
              const nextOrderDays = customer.days_until_next_order;
              const hasNextOrder = nextOrderDays != null;
              const predictedDate = customer.predicted_next_order_date
                ? new Date(customer.predicted_next_order_date).toLocaleDateString()
                : '';
              const lastOrderDate = customer.last_tracked_order_date
                ? new Date(customer.last_tracked_order_date).toLocaleDateString()
                : '';
              const isOverdue = hasNextOrder && (nextOrderDays as number) < 0;
              const nextOrderTitle = !hasNextOrder
                ? ''
                : isOverdue
                  ? t('reports.nextOrderOverdue', { days: Math.abs(nextOrderDays as number), date: predictedDate })
                  : t('reports.nextOrderHint', {
                      date: predictedDate,
                      lastQty: (customer.last_tracked_order_quantity ?? 0).toLocaleString(),
                      lastDate: lastOrderDate,
                    });
              return (
                <tr key={customer.customer_id} className="border-t border-gray-50 hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-800">
                    <button
                      type="button"
                      onClick={() => onOpenCustomer(customer)}
                      className="max-w-sm truncate text-start text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                    >
                      {customer.customer_name || `#${customer.customer_id}`}
                    </button>
                    {customer.tracked_item_groups?.length > 0 && (
                      <div className="mt-1 max-w-sm text-xs font-normal text-gray-400">
                        {customer.tracked_item_groups.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-end font-semibold text-red-700">{formatNIS(customer.aged_debt_amount)}</td>
                  <td className="px-5 py-3 text-end font-semibold text-gray-800">{formatNIS(customer.unpaid_amount)}</td>
                  <td className="px-5 py-3 text-end font-semibold text-gray-800">{(customer.tracked_item_quantity ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-end text-gray-700">{(customer.tracked_item_avg_monthly_3m ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-end text-gray-700">{(customer.tracked_item_avg_monthly_6m ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-center">
                      <Sparkline points={customer.tracked_item_monthly ?? []} />
                    </div>
                  </td>
                  <td className="px-5 py-3 text-end">
                    <span className={`inline-flex min-w-28 items-center justify-center rounded-full border px-2.5 py-1 text-xs font-semibold ${growthClasses[growthStatus] ?? growthClasses.stable}`}>
                      {t(`reports.growth.${growthStatus}`)} {growth > 0 ? '+' : ''}{growth.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-5 py-3 text-end" title={nextOrderTitle}>
                    {!hasNextOrder ? (
                      <span className="text-gray-300">{t('reports.nextOrderNA')}</span>
                    ) : (
                      <span className={`font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-800'}`}>
                        {nextOrderDays}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-8 text-center text-gray-400">
                  {activePresetId !== 'all'
                    ? t('reports.noPresetMatches')
                    : (debouncedQuery ? t('reports.trackedItemNoResults') : t('reports.noTrackedItemData'))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination footer */}
      {sorted.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 text-sm text-gray-500">
          <span>{t('reports.showingOf', { from: fromRow, to: toRow, total: sorted.length })}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={safePage === 0}
              className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ‹ {t('common.prev')}
            </button>
            <span className="text-xs">{safePage + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
              disabled={safePage >= totalPages - 1}
              className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t('common.next')} ›
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
// ── Debt Leaderboard Tables ──────────────────────────────────────────

const TOP_N = 5;

type DebtField = 'aged_debt_amount' | 'unpaid_amount';

function DebtLeaderboardTable({
  data,
  field,
  title,
  onOpenCustomer,
  t,
}: {
  data: CustomerBreakdown[];
  field: DebtField;
  title: string;
  onOpenCustomer: (customer: CustomerBreakdown) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const sorted = useMemo(
    () => [...data].filter((c) => (c[field] ?? 0) > 0).sort((a, b) => (b[field] ?? 0) - (a[field] ?? 0)),
    [data, field],
  );
  const top = sorted.slice(0, TOP_N);
  const maxVal = sorted.length > 0 ? (sorted[0][field] ?? 0) : 1;

  const Row = ({ customer, rank }: { customer: CustomerBreakdown; rank: number }) => {
    const amount = customer[field] ?? 0;
    const barPct = maxVal > 0 ? Math.round((amount / maxVal) * 100) : 0;
    const barColor = field === 'aged_debt_amount' ? 'bg-red-400' : 'bg-orange-400';
    return (
      <div className="flex items-center gap-3 py-2">
        <span className="w-5 shrink-0 text-center text-xs font-semibold text-gray-400">{rank}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="truncate text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left transition-colors"
              onClick={() => onOpenCustomer(customer)}
            >
              {customer.customer_name || `#${customer.customer_id}`}
            </button>
            <span className="shrink-0 text-sm font-bold text-gray-900">{formatNIS(amount)}</span>
          </div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
            <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${barPct}%` }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          {sorted.length > TOP_N && (
            <button
              onClick={() => setModalOpen(true)}
              className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {t('reports.debtLeaderboard.viewAll', { count: sorted.length })}
            </button>
          )}
        </div>
        {sorted.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">{t('reports.debtLeaderboard.noDebt')}</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {top.map((c, i) => <Row key={c.customer_id} customer={c} rank={i + 1} />)}
          </div>
        )}
      </div>

      {/* Full modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
              <button
                onClick={() => setModalOpen(false)}
                className="rounded-full p-1 hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-2 divide-y divide-gray-50">
              {sorted.map((c, i) => <Row key={c.customer_id} customer={c} rank={i + 1} />)}
            </div>
            <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-400 text-center">
              {t('reports.debtLeaderboard.total', { count: sorted.length })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Item Sales Pie Chart ─────────────────────────────────────────────

const ITEM_SALES_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
  '#f97316', '#6366f1',
];

function ItemSalesPieChart({
  data,
  t,
  language,
}: {
  data: ItemGroupSale[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  language: string;
}) {
  const allGroups = useMemo(() => data.map((d) => d.item_group), [data]);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(() => new Set(allGroups));
  const [metric, setMetric] = useState<'amount' | 'quantity'>('amount');

  useEffect(() => {
    setSelectedGroups(new Set(data.map((d) => d.item_group)));
  }, [data]);

  const filteredData = useMemo(
    () => data.filter((d) => selectedGroups.has(d.item_group)),
    [data, selectedGroups],
  );

  const sortedData = useMemo(
    () => [...filteredData].sort((a, b) => b[metric] - a[metric]),
    [filteredData, metric],
  );

  const totalAmount = filteredData.reduce((s, d) => s + d.amount, 0);
  const totalQty = filteredData.reduce((s, d) => s + d.quantity, 0);

  const selectedAnalytics = useMemo(
    () => aggregateSelectedItemSales(data, selectedGroups),
    [data, selectedGroups],
  );
  const monthlyTrendData = useMemo(
    () => selectedAnalytics.monthly.map((point) => ({
      ...point,
      label: new Date(`${point.month}-01T00:00:00`).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', { month: 'short' }),
    })),
    [selectedAnalytics.monthly, language],
  );
  const hasProfitData = selectedAnalytics.hasProfitData;
  const missingCostItems = selectedAnalytics.missingCostItems;

  const allSelected = selectedGroups.size === allGroups.length;

  const toggleGroup = (group: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const chartHeight = Math.max(180, sortedData.length * 36 + 20);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-gray-800">{t('reports.itemSales.title')}</h3>
          <p className="text-sm text-gray-500">{t('reports.itemSales.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-0.5 bg-gray-50 shrink-0">
          <button
            onClick={() => setMetric('amount')}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${metric === 'amount' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {t('reports.itemSales.revenue')}
          </button>
          <button
            onClick={() => setMetric('quantity')}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${metric === 'quantity' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {t('reports.itemSales.quantity')}
          </button>
        </div>
      </div>

      {sortedData.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-12">{t('reports.itemSales.noData')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              layout="vertical"
              data={sortedData}
              margin={{ top: 0, right: 10, bottom: 0, left: 140 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={metric === 'amount' ? (v: number) => formatNIS(v) : (v: number) => v.toLocaleString()}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                dataKey="item_group"
                type="category"
                width={135}
                tick={{ fontSize: 11, fill: '#374151' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value) => [
                  metric === 'amount' ? formatNIS(Number(value ?? 0)) : Number(value ?? 0).toLocaleString(),
                  metric === 'amount' ? t('reports.itemSales.revenue') : t('reports.itemSales.quantity'),
                ]}
              />
              <Bar dataKey={metric} radius={[0, 4, 4, 0]} maxBarSize={28}>
                {sortedData.map((entry, i) => (
                  <Cell key={entry.item_group} fill={ITEM_SALES_COLORS[i % ITEM_SALES_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="border-t border-gray-100 pt-3 flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSelectedGroups(allSelected ? new Set() : new Set(allGroups))}
                className="text-xs px-2.5 py-0.5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
              >
                {allSelected ? t('reports.itemSales.clearAll') : t('reports.itemSales.selectAll')}
              </button>
              {data.map((d, i) => {
                const active = selectedGroups.has(d.item_group);
                return (
                  <button
                    key={d.item_group}
                    onClick={() => toggleGroup(d.item_group)}
                    className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${active ? 'text-white border-transparent' : 'border-gray-200 bg-white text-gray-400 line-through'}`}
                    style={active ? { background: ITEM_SALES_COLORS[i % ITEM_SALES_COLORS.length] } : {}}
                  >
                    {d.item_group}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
              <span>{t('reports.itemSales.totalRow')}</span>
              <div className="flex gap-4">
                <span>{Math.round(totalQty).toLocaleString()} {t('reports.itemSales.quantity').toLowerCase()}</span>
                <span className="font-semibold text-gray-900">{formatNIS(totalAmount)}</span>
              </div>
            </div>
          </div>

          {monthlyTrendData.length > 0 && (
            <div className="border-t border-gray-100 pt-5">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-800">{t('reports.itemSales.monthlyTrend')}</h4>
                <p className="text-xs text-gray-500">{t('reports.itemSales.monthlyTrendSubtitle')}</p>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={monthlyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="quantity" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="amount" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `₪${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value, _name, props) => {
                      const key = (props as { dataKey?: string }).dataKey;
                      return key === 'amount'
                        ? [formatNIS(Number(value)), t('reports.itemSales.revenue')]
                        : [Number(value ?? 0).toLocaleString(), t('reports.itemSales.quantity')];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="quantity" dataKey="quantity" fill="#93c5fd" radius={[4, 4, 0, 0]} name={t('reports.itemSales.quantity')} />
                  <Line yAxisId="amount" type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} name={t('reports.itemSales.revenue')} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {hasProfitData && monthlyTrendData.length > 0 && (
            <div className="border-t border-gray-100 pt-5">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-800">{t('reports.itemSales.profitTrend')}</h4>
                <p className="text-xs text-gray-500">{t('reports.itemSales.profitTrendSubtitle')}</p>
              </div>
              {missingCostItems.length > 0 && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t('reports.itemSales.partialProfitWarning', { items: missingCostItems.join(', ') })}
                </div>
              )}
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₪${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => [formatNIS(Number(value)), t('reports.itemSales.estimatedProfit')]} />
                  <ReferenceLine y={0} stroke="#9ca3af" />
                  <Bar dataKey="profit_amount" name={t('reports.itemSales.estimatedProfit')} radius={[4, 4, 0, 0]}>
                    {monthlyTrendData.map((point) => (
                      <Cell key={point.month} fill={(point.profit_amount ?? 0) < 0 ? '#ef4444' : '#10b981'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ItemOrderForecastTable({
  data,
  onOpenCustomer,
  t,
  language,
}: {
  data: ItemOrderForecastSummary[];
  onOpenCustomer: (customer: { customer_id: number; customer_name: string }) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  language: string;
}) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [sectionExpanded, setSectionExpanded] = useState(false);
  const rows = useMemo(() => normalizeItemOrderForecastSummaries(data), [data]);
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  const formatDate = (value?: string | null) => value
    ? new Date(`${value}T00:00:00`).toLocaleDateString(locale)
    : '—';
  const qualityClasses: Record<string, string> = {
    high: 'border-green-200 bg-green-50 text-green-700',
    medium: 'border-amber-200 bg-amber-50 text-amber-700',
    low: 'border-red-200 bg-red-50 text-red-700',
  };
  const toggle = (itemId: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // "When needed" timeline: bin every non-churned customer forecast by days until
  // its predicted order date, tracking distinct items and summed quantity per bucket.
  const timeline = useMemo(() => {
    const buckets = [
      { key: 'overdue', items: new Set<number>(), quantity: 0 },
      { key: 'thisWeek', items: new Set<number>(), quantity: 0 },
      { key: 'twoWeeks', items: new Set<number>(), quantity: 0 },
      { key: 'thisMonth', items: new Set<number>(), quantity: 0 },
    ];
    for (const item of rows) {
      for (const forecast of item.customer_forecasts) {
        const d = forecast.days_until_order;
        const bucket = d < 0 ? buckets[0] : d <= 7 ? buckets[1] : d <= 14 ? buckets[2] : buckets[3];
        bucket.items.add(item.item_id);
        bucket.quantity += forecast.predicted_quantity;
      }
    }
    return buckets.map((b) => ({ key: b.key, itemCount: b.items.size, quantity: Math.round(b.quantity * 100) / 100 }));
  }, [rows]);

  const bucketTone: Record<string, string> = {
    overdue: 'border-red-200 bg-red-50 text-red-700',
    thisWeek: 'border-amber-200 bg-amber-50 text-amber-700',
    twoWeeks: 'border-blue-200 bg-blue-50 text-blue-700',
    thisMonth: 'border-gray-200 bg-gray-50 text-gray-700',
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <button
          type="button"
          onClick={() => setSectionExpanded((v) => !v)}
          aria-expanded={sectionExpanded}
          aria-label={t(sectionExpanded ? 'reports.collapseSection' : 'reports.expandSection', { section: t('reports.itemForecast.title') })}
          className="flex items-center gap-2 text-start"
        >
          <span className="text-gray-400">{sectionExpanded ? '▾' : '▸'}</span>
          <h3 className="text-base font-semibold text-gray-800">{t('reports.itemForecast.title')}</h3>
        </button>
        <p className="mt-1 text-xs text-gray-500">{t('reports.itemForecast.subtitle')}</p>
      </div>
      {sectionExpanded && (
      <>
      <div className="border-b border-gray-100 px-5 py-4">
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">{t('reports.itemForecast.timelineTitle')}</h4>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {timeline.map((bucket) => (
            <div key={bucket.key} className={`rounded-lg border px-3 py-3 ${bucketTone[bucket.key]}`}>
              <div className="text-xs font-medium">{t(`reports.itemForecast.bucket.${bucket.key}`)}</div>
              <div className="mt-1 text-lg font-bold">{bucket.quantity.toLocaleString()}</div>
              <div className="text-xs opacity-80">{t('reports.itemForecast.bucketItems', { count: bucket.itemCount })}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500">
              <th className="w-10 px-3 py-3" aria-label={t('reports.itemForecast.details')} />
              <th className="px-4 py-3 text-start font-medium">{t('reports.item')}</th>
              <th className="px-4 py-3 text-start font-medium">{t('reports.itemForecast.catalogNumber')}</th>
              <th className="px-4 py-3 text-start font-medium">{t('reports.filterItemGroup')}</th>
              <th className="px-4 py-3 text-end font-medium">{t('reports.itemForecast.quantityNeeded7d')}</th>
              <th className="px-4 py-3 text-end font-medium">{t('reports.itemForecast.quantityNeeded30d')}</th>
              <th className="px-4 py-3 text-end font-medium" title={t('reports.itemForecast.earliestNeedHint')}>
                {t('reports.itemForecast.earliestNeed')}
              </th>
              <th className="px-4 py-3 text-end font-medium">{t('reports.itemForecast.customersDue')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const isExpanded = expanded.has(item.item_id);
              const hasDetails = item.customer_forecasts.length > 0 || item.churned_customers.length > 0;
              return (
                <Fragment key={item.item_id}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => toggle(item.item_id)}
                        disabled={!hasDetails}
                        aria-expanded={isExpanded}
                        aria-label={t('reports.itemForecast.toggleDetails', { item: item.item_name })}
                        className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-default disabled:opacity-25"
                      >
                        {isExpanded ? '▾' : '▸'}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">{item.item_name}</td>
                    <td className="px-4 py-3 text-gray-500">{item.catalog_number || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{item.item_group || '—'}</td>
                    <td className="px-4 py-3 text-end">
                      <span className="font-semibold text-gray-900">{item.next_7_days_quantity.toLocaleString()}</span>
                      {item.overdue_quantity > 0 && (
                        <div className="text-xs font-medium text-red-600">
                          {t('reports.itemForecast.overdueQuantity', { quantity: item.overdue_quantity.toLocaleString() })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end font-semibold text-gray-900">{item.total_predicted_quantity.toLocaleString()}</td>
                    <td className="px-4 py-3 text-end text-gray-700" title={t('reports.itemForecast.earliestNeedHint')}>
                      {formatDate(item.earliest_predicted_date)}
                      {item.customer_forecasts[0]?.days_until_order < 0 && (
                        <div className="text-xs font-medium text-red-600">
                          {t('reports.itemForecast.overdue')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end text-gray-700">
                      {item.customers_due_count}
                      {item.insufficient_history_customers > 0 && (
                        <div className="text-xs text-gray-400" title={t('reports.itemForecast.insufficientHint')}>
                          {t('reports.itemForecast.insufficientCount', { count: item.insufficient_history_customers })}
                        </div>
                      )}
                      {item.churned_customers_count > 0 && (
                        <div className="text-xs font-medium text-orange-500" title={t('reports.itemForecast.churnedHint')}>
                          {t('reports.itemForecast.churnedCount', { count: item.churned_customers_count })}
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && item.customer_forecasts.map((forecast) => (
                    <tr key={`${item.item_id}-${forecast.customer_id}`} className="border-t border-blue-50 bg-blue-50/40">
                      <td />
                      <td colSpan={2} className="px-4 py-3 ltr:pl-8 rtl:pr-8">
                        <button
                          type="button"
                          onClick={() => onOpenCustomer(forecast)}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {forecast.customer_name || `#${forecast.customer_id}`}
                        </button>
                        <div className="mt-0.5 text-xs text-gray-400">
                          {t('reports.itemForecast.historySummary', {
                            count: forecast.history_order_count,
                            cadence: forecast.median_cadence_days.toLocaleString(),
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-start">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${qualityClasses[forecast.quality] || qualityClasses.low}`}>
                          {t(`reports.itemForecast.quality.${forecast.quality}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-end font-semibold text-gray-800">{forecast.predicted_quantity.toLocaleString()}</td>
                      <td className="px-4 py-3 text-end">
                        <span className={forecast.days_until_order < 0 ? 'font-semibold text-red-600' : 'text-gray-700'}>
                          {formatDate(forecast.predicted_order_date)}
                        </span>
                        <div className="text-xs text-gray-400">
                          {forecast.days_until_order < 0
                            ? t('reports.itemForecast.overdueDays', { days: Math.abs(forecast.days_until_order) })
                            : t('reports.itemForecast.inDays', { days: forecast.days_until_order })}
                        </div>
                      </td>
                      <td colSpan={2} className="px-4 py-3 text-end text-xs text-gray-500">
                        {t('reports.itemForecast.lastOrder', {
                          quantity: forecast.last_order_quantity.toLocaleString(),
                          date: formatDate(forecast.last_order_date),
                        })}
                      </td>
                    </tr>
                  ))}
                  {isExpanded && item.churned_customers.length > 0 && (
                    <>
                      <tr className="border-t border-orange-100 bg-orange-50/50">
                        <td />
                        <td colSpan={7} className="px-4 py-2 ltr:pl-8 rtl:pr-8 text-xs font-semibold uppercase tracking-wide text-orange-600">
                          {t('reports.itemForecast.churnedTitle')}
                          <span className="ltr:ml-1 rtl:mr-1 font-normal normal-case text-orange-400">
                            {t('reports.itemForecast.churnedSubtitle')}
                          </span>
                        </td>
                      </tr>
                      {item.churned_customers.map((forecast) => (
                        <tr key={`${item.item_id}-churned-${forecast.customer_id}`} className="border-t border-orange-50 bg-orange-50/30">
                          <td />
                          <td colSpan={3} className="px-4 py-3 ltr:pl-8 rtl:pr-8">
                            <button
                              type="button"
                              onClick={() => onOpenCustomer(forecast)}
                              className="font-medium text-orange-700 hover:text-orange-900 hover:underline"
                            >
                              {forecast.customer_name || `#${forecast.customer_id}`}
                            </button>
                            <div className="mt-0.5 text-xs text-orange-400">
                              {t('reports.itemForecast.lastOrderedDaysAgo', { days: forecast.days_since_last_order })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-end font-semibold text-gray-600">{forecast.last_order_quantity.toLocaleString()}</td>
                          <td colSpan={3} className="px-4 py-3 text-end text-xs text-gray-500">
                            {t('reports.itemForecast.lastOrder', {
                              quantity: forecast.last_order_quantity.toLocaleString(),
                              date: formatDate(forecast.last_order_date),
                            })}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-10 text-center text-gray-400">{t('reports.itemForecast.empty')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}
    </section>
  );
}

// ── Main Component ───────────────────────────────────────────────────

const STALE_HOURS = 24; // data older than this is considered stale

export default function Reports() {
  const { t, i18n } = useTranslation();
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportData | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<{ customer_id: number; customer_name: string } | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getReport({ period: 'last_6_months' });
      if (res.data?.status === 'no_data') {
        setData(null);
        setError(res.data.message || t('reports.noData'));
      } else {
        setData(res.data);
        setGeneratedAt(res.data?.generated_at || null);
      }
    } catch {
      setError(t('reports.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    // Capture the click timestamp so the poll can exit as soon as the server
    // returns a report that was generated *after* the user clicked Refresh.
    // This is more robust than comparing generated_at strings across closure
    // boundaries, which can be stale if React hasn't re-created the callback yet.
    const clickedAt = Date.now();
    try {
      await generateReport({ period: 'last_6_months' });
      let attempts = 0;
      const poll = async () => {
        while (attempts < 200) {
          await new Promise((r) => setTimeout(r, 3000));
          attempts++;
          try {
            const res = await getReport({ period: 'last_6_months' });
            const resGeneratedAt = res.data?.generated_at;
            if (
              res.data &&
              res.data.status !== 'no_data' &&
              resGeneratedAt &&
              new Date(resGeneratedAt).getTime() > clickedAt
            ) {
              setData(res.data);
              setGeneratedAt(resGeneratedAt || null);
              return;
            }
          } catch { /* keep polling */ }
        }
        await fetchReport();
      };
      await poll();
    } catch {
      setError(t('reports.error'));
    } finally {
      setRefreshing(false);
    }
  }, [t, fetchReport]);

  const handleBackfill = useCallback(async () => {
    setBackfilling(true);
    setError(null);
    try {
      await backfillReportLineItems();
    } catch {
      setError(t('reports.error'));
    } finally {
      setBackfilling(false);
    }
  }, [t]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const isStale = useMemo(() => {
    if (!generatedAt) return false;
    const ageMs = Date.now() - new Date(generatedAt).getTime();
    return ageMs > STALE_HOURS * 60 * 60 * 1000;
  }, [generatedAt]);

  const staleHours = useMemo(() => {
    if (!generatedAt) return 0;
    return Math.floor((Date.now() - new Date(generatedAt).getTime()) / (60 * 60 * 1000));
  }, [generatedAt]);

  const debtTrendData = useMemo(() => {
    if (!data) return [];
    return data.debt_kpi_history.map((point) => ({
      ...point,
      label: point.month.slice(5),
    }));
  }, [data]);

  const revenueTrendData = useMemo(() => {
    if (!data) return [];
    return (data.monthly_revenue ?? []).map((point) => ({
      ...point,
      label: new Date(`${point.month}-01T00:00:00`).toLocaleDateString(
        i18n.language === 'he' ? 'he-IL' : 'en-US',
        { month: 'short' },
      ),
    }));
  }, [data, i18n.language]);

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('reports.subtitle')}</p>
          {generatedAt && (
            <p className={`text-xs mt-1 ${isStale ? 'text-amber-500 font-medium' : 'text-gray-400'}`}>
              {t('reports.lastUpdated', { date: new Date(generatedAt).toLocaleString(i18n.language === 'he' ? 'he-IL' : 'en-US') })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing || backfilling}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:border-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refreshing ? (
              <>
                <span className="animate-spin inline-block h-4 w-4 border-2 border-primary-300 border-t-primary-600 rounded-full" />
                {t('reports.refreshing')}
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('reports.refresh')}
              </>
            )}
          </button>
          <button
            onClick={handleBackfill}
            disabled={backfilling || refreshing}
            title="Backfill cached line-items for historical invoices so future refreshes are fast"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {backfilling ? (
              <>
                <span className="animate-spin inline-block h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full" />
                {t('reports.backfilling')}
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {t('reports.backfill')}
              </>
            )}
          </button>
        </div>
      </div>

      {data && !loading && (
        <div className="text-sm text-gray-500">
          {t('reports.analyticsRange', {
            from: formatFilterDate(data.from_date, i18n.language),
            to: formatFilterDate(data.to_date, i18n.language),
          })}
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Stale data banner */}
      {isStale && data && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>{t('reports.staleWarning', { hours: staleHours })}</span>
          <button
            onClick={handleRefresh}
            disabled={refreshing || backfilling}
            className="ltr:ml-auto rtl:mr-auto shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            {t('reports.refresh')}
          </button>
        </div>
      )}

      {/* Content is visually dimmed when stale, but remains inspectable. */}
      <div className={isStale && data && !loading ? 'opacity-60' : ''}>

      {/* KPIs: Revenue, projected revenue, old debt, all debt, ratio */}
      {(data || loading) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard
            label={t('reports.revenueToDate')}
            value={data?.revenue_to_date ?? 0}
            format="currency"
            loading={loading}
            t={t}
          />
          <KpiCard
            label={t('reports.projectedRevenueYearEnd')}
            value={data?.projected_revenue_year_end ?? 0}
            format="currency"
            loading={loading}
            t={t}
          />
          <KpiCard
            label={t('reports.oldDebt')}
            value={data?.total_old_debt ?? 0}
            format="currency"
            loading={loading}
            invertChange
            t={t}
          />
          <KpiCard
            label={t('reports.totalDebt')}
            value={data?.total_all_debt ?? 0}
            format="currency"
            loading={loading}
            invertChange
            t={t}
          />
          <KpiCard
            label={t('reports.oldDebtRatio')}
            value={data?.old_debt_ratio ?? 0}
            prevValue={data?.prev_old_debt_ratio}
            format="percent"
            loading={loading}
            invertChange
            t={t}
          />
        </div>
      )}

      {data && !loading && (
        <>
          {/* Revenue Graph with dotted projection */}
          {revenueTrendData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">{t('reports.monthlyRevenue')}</h3>
                  <p className="text-sm text-gray-500">{t('reports.monthlyRevenueSubtitle')}</p>
                </div>
                <span className="text-sm text-gray-500">
                  {t('reports.projectedRevenueYearEnd')}: {formatNIS(data.projected_revenue_year_end)}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={revenueTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₪${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value, _name, props) => {
                      const key = (props as { dataKey?: string }).dataKey;
                      return [
                        formatNIS(Number(value)),
                        key === 'projected_revenue' ? t('reports.projectedRevenue') : t('reports.actualRevenue'),
                      ];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#93c5fd" radius={[4, 4, 0, 0]} barSize={24} name={t('reports.actualRevenue')} />
                  <Line
                    type="monotone"
                    dataKey="projected_revenue"
                    stroke="#2563eb"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3, fill: '#fff', stroke: '#2563eb', strokeWidth: 2 }}
                    activeDot={{ r: 5 }}
                    name={t('reports.projectedRevenue')}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Debt Ratio Graph with reference lines */}
          {debtTrendData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <span className="flex items-center gap-1.5">
                  <h3 className="text-base font-semibold text-gray-800">{t('reports.oldDebtHistory')}</h3>
                  <span className="group relative flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 cursor-help" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-72 -translate-x-1/2 rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                      {t('reports.oldDebtHistoryTooltip', { days: 60 })}
                    </span>
                  </span>
                </span>
                <span className="text-sm text-gray-500">
                  {t('reports.oldDebt')}: {formatNIS(data.total_old_debt)} &nbsp;|&nbsp; {t('reports.totalDebt')}: {formatNIS(data.total_all_debt)}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={debtTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="amount" tick={{ fontSize: 12 }} tickFormatter={(v) => `₪${(Number(v) / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="ratio" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${Number(v).toFixed(0)}%`} />
                  <Tooltip
                    formatter={(value, _name, props) => {
                      const key = (props as { dataKey?: string }).dataKey;
                      if (key === 'old_debt_ratio') return [`${Number(value).toFixed(1)}%`, t('reports.oldDebtRatio')];
                      if (key === 'old_debt_amount') return [formatNIS(Number(value)), t('reports.oldDebt')];
                      return [formatNIS(Number(value)), t('reports.totalDebt')];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <Legend />
                  {/* Reference lines for current old debt and total debt */}
                  {data.total_old_debt != null && <ReferenceLine yAxisId="amount" y={data.total_old_debt} stroke="#d1d5db" strokeDasharray="4 4" label={{ value: formatNIS(data.total_old_debt), position: 'left', fill: '#9ca3af', fontSize: 11 }} />}
                  {data.total_all_debt != null && <ReferenceLine yAxisId="amount" y={data.total_all_debt} stroke="#d1d5db" strokeDasharray="4 4" label={{ value: formatNIS(data.total_all_debt), position: 'left', fill: '#9ca3af', fontSize: 11 }} />}
                  <Bar yAxisId="amount" dataKey="total_debt_amount" fill="#dbeafe" radius={[4, 4, 0, 0]} barSize={22} name={t('reports.totalDebt')} />
                  <Bar yAxisId="amount" dataKey="old_debt_amount" fill="#fca5a5" radius={[4, 4, 0, 0]} barSize={22} name={t('reports.oldDebt')} />
                  <Line
                    yAxisId="ratio"
                    dataKey="old_debt_ratio"
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    name={t('reports.oldDebtRatio')}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top Debt Tables */}
          {data.customer_breakdown.some((c) => (c.aged_debt_amount ?? 0) > 0 || (c.unpaid_amount ?? 0) > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DebtLeaderboardTable
                data={data.customer_breakdown}
                field="aged_debt_amount"
                title={t('reports.debtLeaderboard.oldDebtTitle')}
                onOpenCustomer={setSelectedCustomer}
                t={t}
              />
              <DebtLeaderboardTable
                data={data.customer_breakdown}
                field="unpaid_amount"
                title={t('reports.debtLeaderboard.totalDebtTitle')}
                onOpenCustomer={setSelectedCustomer}
                t={t}
              />
            </div>
          )}

          {/* Item Sales Pie Chart */}
          {(data.item_group_sales ?? []).length > 0 && (
            <ItemSalesPieChart data={data.item_group_sales} t={t} language={i18n.language} />
          )}

          <ItemOrderForecastTable
            data={data.item_order_forecasts ?? []}
            onOpenCustomer={setSelectedCustomer}
            t={t}
            language={i18n.language}
          />

          {/* Tracked Item Consumption Table */}
          <TrackedItemConsumptionTable data={data.customer_breakdown} onOpenCustomer={setSelectedCustomer} t={t} />
        </>
      )}

      </div>{/* end stale-dimming wrapper */}

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <button type="button" aria-expanded={summaryExpanded} aria-controls="reports-daily-summary"
          onClick={() => {
            setSummaryLoaded(true);
            setSummaryExpanded(value => !value);
          }}
          className="flex w-full items-center justify-between gap-3 p-5 text-start">
          <span>
            <span className="block font-semibold">{t('reports.whatsappSummary.title')}</span>
            <span className="block text-sm text-gray-500">{t('reports.whatsappSummary.description')}</span>
          </span>
          <span aria-hidden="true">{summaryExpanded ? '−' : '+'}</span>
        </button>
        <div id="reports-daily-summary" hidden={!summaryExpanded} className="p-5 pt-0">
          {summaryLoaded && <DailySummarySettings />}
        </div>
      </section>

      {selectedCustomer && (
        <CustomerDetailsModal
          customerId={selectedCustomer.customer_id}
          displayName={selectedCustomer.customer_name || `#${selectedCustomer.customer_id}`}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
}
